import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"
import _imports_0 from '@/assets.app/images/onboarding/book-search-empty.svg'


const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = { class: "onboarding-book-selection-20710__results" }
const _hoisted_3 = {
  key: 0,
  class: "onboarding-book-selection-20710__empty",
  role: "status"
}
const _hoisted_4 = {
  key: 1,
  class: "onboarding-book-selection-20710__options"
}
const _hoisted_5 = ["aria-label", "aria-pressed", "onClick"]
const _hoisted_6 = { class: "onboarding-book-selection-20710__footer" }

import { computed, onBeforeMount, ref, watch } from 'vue'

import { Mixpanel } from '@/analytics/mixpanel'
import { debounce } from '@/helpers/form'
import { cacheBust } from '@/models/doc'
import type { QuestionnaireView } from '@/models/onboarding'
import {
  INITIAL_BOOKS,
  sampleGuideFromDoc,
  SEARCH_DEBOUNCE_MS,
  SEARCH_FETCH_LIMIT,
} from '@/models/onboarding.book-selection-20710'
import type {
  BookSelection20710Page,
  SampleGuideChoice20710,
} from '@/models/onboarding.types'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import CircleSpinner from '@/components/ui/CircleSpinner.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import Layout from '../Layout.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'BookSelection20710',
  props: {
    view: {}
  },
  setup(__props: any) {

const props = __props

const page = computed(() => props.view.currentPage as BookSelection20710Page)

const term = ref('')
const books = ref<SampleGuideChoice20710[]>([...INITIAL_BOOKS])
const selectedSlug = ref<string | null>(null)
const isSearching = ref(false)
const query = computed(() => term.value.trim())

const showEmpty = computed(
  () => query.value !== '' && !isSearching.value && books.value.length === 0,
)

const debouncedSearch = debounce(() => {
  void runSearch()
}, SEARCH_DEBOUNCE_MS)

watch(term, () => {
  if (!query.value) {
    isSearching.value = false
    books.value = [...INITIAL_BOOKS]
    return
  }

  isSearching.value = true
  debouncedSearch()
})

function isSelected(book: SampleGuideChoice20710): boolean {
  return selectedSlug.value === book.url_slug
}

function selectBook(book: SampleGuideChoice20710): void {
  selectedSlug.value = book.url_slug
  props.view.setCurrentChoice(book)
}

async function runSearch(): Promise<void> {
  const submitted = query.value
  if (!submitted) {
    return
  }

  try {
    const result = await backend.getDocSearch(
      'book',
      submitted,
      SEARCH_FETCH_LIMIT,
      0,
    )
    if (query.value !== submitted) {
      return
    }

    const docs = result.data ?? []
    books.value = docs.map(sampleGuideFromDoc)
    if (docs.length === 0) {
      Mixpanel.trackOnboardingBookSearchNoResults20710(submitted)
    }
  } catch (error) {
    if (query.value !== submitted) {
      return
    }
    Sentry.captureException(error)
  } finally {
    if (query.value === submitted) {
      isSearching.value = false
    }
  }
}

onBeforeMount(() => {
  for (const book of INITIAL_BOOKS) {
    if (!book.cover_image) {
      continue
    }
    const image = new Image()
    image.src = cacheBust(book.cover_image)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Layout, {
    class: "onboarding-book-selection-20710",
    title: page.value.title,
    subtitle: page.value.subtitle,
    scrollDefaultSlot: true
  }, {
    default: _withCtx(() => [
      _createElementVNode("form", {
        class: "onboarding-book-selection-20710__search",
        onSubmit: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
      }, [
        _cache[3] || (_cache[3] = _createElementVNode("span", {
          class: "onboarding-book-selection-20710__search-icon iconfont isearch",
          "aria-hidden": "true"
        }, null, -1)),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((term).value = $event)),
          class: "onboarding-book-selection-20710__input",
          type: "search",
          placeholder: "Search any book or an idea",
          "aria-label": "Search any book or an idea",
          "aria-busy": isSearching.value,
          autocomplete: "off"
        }, null, 8, _hoisted_1), [
          [_vModelText, term.value]
        ]),
        (isSearching.value)
          ? (_openBlock(), _createBlock(CircleSpinner, {
              key: 0,
              class: "onboarding-book-selection-20710__spinner",
              size: 18,
              color: "#6b6d76"
            }))
          : _createCommentVNode("", true)
      ], 32),
      _createElementVNode("div", _hoisted_2, [
        (showEmpty.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_3, [...(_cache[4] || (_cache[4] = [
              _createElementVNode("img", {
                class: "onboarding-book-selection-20710__empty-icon",
                src: _imports_0,
                alt: ""
              }, null, -1),
              _createElementVNode("span", { class: "onboarding-book-selection-20710__empty-copy" }, [
                _createElementVNode("span", { class: "onboarding-book-selection-20710__empty-title" }, " No results found. "),
                _createElementVNode("span", { class: "onboarding-book-selection-20710__empty-hint" }, " Try a new search, or check the spelling. ")
              ], -1)
            ]))]))
          : (_openBlock(), _createElementBlock("ul", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(books.value, (book) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: book.url_slug
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["onboarding-book-selection-20710__option", {
              'onboarding-book-selection-20710__option--selected':
                isSelected(book),
            }]),
                    "aria-label": `${book.title} by ${book.author}`,
                    "aria-pressed": isSelected(book),
                    onClick: ($event: any) => (selectBook(book))
                  }, [
                    _createVNode(BookCardThumbnail, {
                      class: "onboarding-book-selection-20710__thumb",
                      doc: book,
                      coverClass: "onboarding-book-selection-20710__cover",
                      fallbackCoverClass: "onboarding-book-selection-20710__fallback",
                      cacheBust: ""
                    }, null, 8, ["doc"]),
                    _cache[5] || (_cache[5] = _createElementVNode("span", {
                      class: "onboarding-book-selection-20710__radio",
                      "aria-hidden": "true"
                    }, null, -1))
                  ], 10, _hoisted_5)
                ]))
              }), 128))
            ]))
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(AppButton, {
          class: "onboarding-book-selection-20710__continue",
          disabled: !selectedSlug.value,
          loading: __props.view.isLoadingNextPage,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (__props.view.continue()))
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode(" Build my path ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled", "loading"])
      ])
    ]),
    _: 1
  }, 8, ["title", "subtitle"]))
}
}

})